import React, { useState, useEffect, useRef, useCallback } from 'react';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import { supabase } from '../config/supabase';

// ── myGAP "Laporan Analisis Residu Racun Perosak Hasil Pertanian" (WI-01 / WI-02) ──
// Six ordered upload slots: lab residue report + Borang RF2 for attempts 1-3.
// Files live in the PRIVATE storage bucket 'mygap-residue' (never the public
// 'images' bucket) at '<farm_id>/<season_id or 0>/<slot>/<timestamp>-<file>',
// with one metadata row per file in public.mygap_residue_docs. Files are opened
// and merged into the export through short-lived signed URLs only.
// Schema + RLS: supabase/migrations/20260924_mygap_notes_and_residue.sql.

export const RESIDUE_BUCKET = 'mygap-residue';
export const RESIDUE_TABLE = 'mygap_residue_docs';
export const RESIDUE_HEADING = 'Laporan Analisis Residu Racun Perosak Hasil Pertanian';
// label = exact wording from the 23 Sep 2026 brief (screen); pdfLabel = the same
// without the "(Ruangan Muat Naik …)" hint, for the PDF contents / image pages.
export const RESIDUE_SLOTS = [
  { slot: 1, label: 'Laporan Analisis Makmal Residu 1 (Ruangan Muat Naik Laporan)', pdfLabel: 'Laporan Analisis Makmal Residu 1' },
  { slot: 2, label: 'Borang Siasatan Ketidakpatuhan Residu Racun Perosak 1 (Borang RF2) (Ruangan Muat Naik Borang)', pdfLabel: 'Borang Siasatan Ketidakpatuhan Residu Racun Perosak 1 (Borang RF2)' },
  { slot: 3, label: 'Laporan Analisis Makmal Residu 2 (Ruangan Muat Naik Laporan)', pdfLabel: 'Laporan Analisis Makmal Residu 2' },
  { slot: 4, label: 'Borang Siasatan Ketidakpatuhan Residu Racun Perosak 2 (Borang RF2) (Ruangan Muat Naik Borang)', pdfLabel: 'Borang Siasatan Ketidakpatuhan Residu Racun Perosak 2 (Borang RF2)' },
  { slot: 5, label: 'Laporan Analisis Makmal Residu 3 (Ruangan Muat Naik Laporan)', pdfLabel: 'Laporan Analisis Makmal Residu 3' },
  { slot: 6, label: 'Borang Siasatan Ketidakpatuhan Residu Racun Perosak 3 (Borang RF2) (Ruangan Muat Naik Borang)', pdfLabel: 'Borang Siasatan Ketidakpatuhan Residu Racun Perosak 3 (Borang RF2)' },
];
export const RESIDUE_MAX_BYTES = 45 * 1024 * 1024;   // bucket limit is 45 MiB (47185920)
export const RESIDUE_MAX_PER_SLOT = 10;
// Signed URLs are created right before use (open / merge), so keep them short.
export const RESIDUE_SIGNED_URL_TTL = 300;           // seconds

export const residueSlotDef = (slot) => RESIDUE_SLOTS.find(s => s.slot === Number(slot)) || null;

// Selected cycle → season_id for the rows (null when "All Cycles" / no cycle).
export const residueSeasonId = (cycleId) => {
  const n = Number(cycleId);
  return Number.isInteger(n) && n > 0 ? n : null;
};

// The bucket only accepts these three types, so send an explicit content type
// (some browsers give '' or the non-standard 'image/jpg').
export const residueMimeFor = (file) => {
  const t = String(file?.type || '').toLowerCase();
  if (t === 'application/pdf' || t === 'image/png' || t === 'image/jpeg') return t;
  if (t === 'image/jpg' || t === 'image/pjpeg') return 'image/jpeg';
  const n = String(file?.name || '').toLowerCase();
  if (/\.pdf$/.test(n)) return 'application/pdf';
  if (/\.png$/.test(n)) return 'image/png';
  if (/\.jpe?g$/.test(n)) return 'image/jpeg';
  return '';
};

const safeFileName = (name) => {
  const s = String(name || 'fail').replace(/[^a-zA-Z0-9._-]/g, '_').replace(/^\.+/, '_');
  return s.length > 120 ? s.slice(s.length - 120) : (s || 'fail');
};

// Files for one farm + cycle (season_id NULL when no cycle), slot order then upload order.
export async function listResidueDocs(farmId, seasonId) {
  let q = supabase
    .from(RESIDUE_TABLE)
    .select('id, slot, path, file_name, mime, size_bytes, uploaded_at')
    .eq('farm_id', farmId);
  q = seasonId ? q.eq('season_id', seasonId) : q.is('season_id', null);
  const { data, error } = await q
    .order('slot', { ascending: true })
    .order('uploaded_at', { ascending: true })
    .order('id', { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function residueSignedUrl(path, ttl = RESIDUE_SIGNED_URL_TTL) {
  const { data, error } = await supabase.storage.from(RESIDUE_BUCKET).createSignedUrl(path, ttl);
  if (error) throw error;
  if (!data?.signedUrl) throw new Error('Pautan fail tidak dapat dijana');
  return data.signedUrl;
}

// pdf-lib's standard fonts only encode WinAnsi (CP1252); anything else makes
// drawText throw. Keep Latin-1 + the CP1252 extras, strip accents from other
// Latin letters, and replace the rest with '?'.
const WIN_ANSI_EXTRA = '€‚ƒ„…†‡ˆ‰Š‹ŒŽ‘’“”•–—˜™š›œžŸ';
export const pdfSafeText = (value) => {
  let out = '';
  for (const ch of String(value ?? '')) {
    const c = ch.codePointAt(0);
    if (c === 9 || c === 10 || c === 13) { out += ' '; continue; }
    if ((c >= 0x20 && c <= 0x7e) || (c >= 0xa0 && c <= 0xff) || WIN_ANSI_EXTRA.includes(ch)) { out += ch; continue; }
    let base = '';
    try { base = ch.normalize('NFKD').replace(/[̀-ͯ]/g, ''); } catch { base = ''; }
    out += /^[\x20-\x7e]+$/.test(base) ? base : '?';
  }
  return out;
};

const wrapPdfText = (text, font, size, maxWidth) => {
  const words = pdfSafeText(text).split(/\s+/).filter(Boolean);
  const lines = [];
  let cur = '';
  const fits = (s) => font.widthOfTextAtSize(s, size) <= maxWidth;
  words.forEach((w) => {
    let word = w;
    // Hard-break a single word that is wider than the line (long file names).
    while (!fits(word) && word.length > 1) {
      let cut = word.length - 1;
      while (cut > 1 && !fits(word.slice(0, cut))) cut--;
      if (cur) { lines.push(cur); cur = ''; }
      lines.push(word.slice(0, cut));
      word = word.slice(cut);
    }
    const next = cur ? `${cur} ${word}` : word;
    if (fits(next)) cur = next;
    else { if (cur) lines.push(cur); cur = word; }
  });
  if (cur) lines.push(cur);
  return lines.length ? lines : [''];
};

// Heading page(s) of the residue export: title, farm, cycle, date and the list
// of files that follow (slot order). entries: [{ slot, fileName }].
export async function buildResidueHeadingPdf({ farmName, cycleLabel, dateStr, entries, logos }) {
  const doc = await PDFDocument.create();
  try { doc.setTitle(RESIDUE_HEADING); } catch { /* metadata is optional */ }
  const font = await doc.embedFont(StandardFonts.Helvetica);
  const bold = await doc.embedFont(StandardFonts.HelveticaBold);
  const W = 595.28, H = 841.89, M = 50, BOTTOM = 60;
  const dark = rgb(0.13, 0.13, 0.13), grey = rgb(0.42, 0.42, 0.42), green = rgb(0.086, 0.5, 0.2);

  let page = doc.addPage([W, H]);
  const header = (p) => {
    p.drawText('Generated by Kapitani taniLytics Software', { x: M, y: H - 30, size: 8, font, color: grey });
    const right = 'myGAP';
    p.drawText(right, { x: W - M - font.widthOfTextAtSize(right, 8), y: H - 30, size: 8, font, color: grey });
  };
  header(page);
  let y = H - 50;

  const embedLogo = async (dataUrl) => {
    if (!dataUrl) return null;
    try { return await doc.embedPng(dataUrl); } catch { /* not a png */ }
    try { return await doc.embedJpg(dataUrl); } catch { return null; }
  };
  const [cropLogo, mygapLogo] = await Promise.all([embedLogo(logos?.crop), embedLogo(logos?.mygap)]);
  const LOGO_H = 60;
  if (cropLogo) {
    const w = cropLogo.width * (LOGO_H / cropLogo.height);
    page.drawImage(cropLogo, { x: M, y: y - LOGO_H, width: w, height: LOGO_H });
  }
  if (mygapLogo) {
    const w = mygapLogo.width * (LOGO_H / mygapLogo.height);
    page.drawImage(mygapLogo, { x: W - M - w, y: y - LOGO_H, width: w, height: LOGO_H });
  }
  y -= LOGO_H + 34;

  wrapPdfText(RESIDUE_HEADING.toUpperCase(), bold, 18, W - 2 * M).forEach((line) => {
    page.drawText(line, { x: (W - bold.widthOfTextAtSize(line, 18)) / 2, y, size: 18, font: bold, color: dark });
    y -= 24;
  });
  y -= 4;
  page.drawLine({ start: { x: M, y }, end: { x: W - M, y }, thickness: 1.2, color: green });
  y -= 30;

  const LABEL_W = 70;
  [['Ladang', farmName || '-'], ['Kitaran', cycleLabel || '-'], ['Tarikh', dateStr || '-']].forEach(([k, v]) => {
    page.drawText(k, { x: M, y, size: 11, font: bold, color: dark });
    page.drawText(':', { x: M + LABEL_W - 10, y, size: 11, font: bold, color: dark });
    wrapPdfText(v, font, 11, W - 2 * M - LABEL_W).forEach((line) => {
      page.drawText(line, { x: M + LABEL_W, y, size: 11, font, color: dark });
      y -= 16;
    });
    y -= 4;
  });
  y -= 16;

  const ensureRoom = (need) => {
    if (y - need >= BOTTOM) return;
    page = doc.addPage([W, H]);
    header(page);
    y = H - 60;
  };

  page.drawText('Kandungan', { x: M, y, size: 12, font: bold, color: dark });
  y -= 20;
  const bySlot = new Map();
  (entries || []).forEach((e) => {
    if (!bySlot.has(e.slot)) bySlot.set(e.slot, []);
    bySlot.get(e.slot).push(e);
  });
  [...bySlot.keys()].sort((a, b) => a - b).forEach((slot) => {
    const def = residueSlotDef(slot);
    wrapPdfText(`${slot}. ${def ? def.pdfLabel : `Slot ${slot}`}`, bold, 10, W - 2 * M).forEach((line) => {
      ensureRoom(16);
      page.drawText(line, { x: M, y, size: 10, font: bold, color: dark });
      y -= 14;
    });
    bySlot.get(slot).forEach((e) => {
      wrapPdfText(`- ${e.fileName || 'fail'}`, font, 9, W - 2 * M - 16).forEach((line) => {
        ensureRoom(14);
        page.drawText(line, { x: M + 16, y, size: 9, font, color: grey });
        y -= 12;
      });
    });
    y -= 6;
  });

  ensureRoom(40);
  y -= 10;
  wrapPdfText('Fail dilampirkan selepas halaman ini mengikut susunan slot 1 hingga 6. Hanya slot yang mempunyai fail disertakan. Keputusan lulus / gagal tidak direkodkan dalam sistem; rujuk laporan makmal.', font, 8, W - 2 * M)
    .forEach((line) => {
      page.drawText(line, { x: M, y, size: 8, font, color: grey });
      y -= 11;
    });

  return await doc.save();
}

// ── Screen section ────────────────────────────────────────────────────────────
// Same interaction as the Form 1-1 "Dokumen Sokongan (Lampiran 1)" uploader:
// choose files (jpeg/png/pdf, 45 MB), a listed file opens in a new tab, "Buang"
// removes it. Scoped to the selected cycle (season_id NULL when none).
// access (from MyGAPReport's check, same helpers as the RLS policies):
// 'unknown' | 'write' | 'read' (list + open only) | 'none' (nothing: RLS would
// just return an empty list and refuse uploads with a raw error).
const MyGAPResidueSection = ({ farm, seasonId, cycleLabel, access = 'unknown' }) => {
  const noAccess = access === 'none';
  const readOnly = access === 'read';
  const farmId = Number(farm?.id) || null;
  const sid = residueSeasonId(seasonId);
  const scope = `${farmId}:${sid ?? 'none'}`;
  const scopeRef = useRef(scope);
  scopeRef.current = scope;

  const [docs, setDocs] = useState([]);
  const [status, setStatus] = useState('loading');   // 'loading' | 'ready' | 'error'
  const [loadErr, setLoadErr] = useState('');
  const [busySlot, setBusySlot] = useState(0);       // slot being uploaded to (0 = none)
  const [busyId, setBusyId] = useState(null);        // doc being removed
  const [err, setErr] = useState('');

  const reload = useCallback(async () => {
    const myScope = `${farmId}:${sid ?? 'none'}`;
    if (!farmId || noAccess) { setDocs([]); setStatus('ready'); return; }
    try {
      const rows = await listResidueDocs(farmId, sid);
      if (scopeRef.current !== myScope) return;      // farm / cycle changed meanwhile
      setDocs(rows);
      setLoadErr('');
      setStatus('ready');
    } catch (e) {
      if (scopeRef.current !== myScope) return;
      console.warn('myGAP residue: list failed', e);
      setLoadErr(e?.message || String(e));
      setStatus('error');
    }
  }, [farmId, sid, noAccess]);

  useEffect(() => {
    setDocs([]);
    setErr('');
    setStatus('loading');
    reload();
  }, [reload]);

  const filesFor = (slot) => docs.filter(d => Number(d.slot) === slot);

  const handleUpload = async (slot, fileList) => {
    setErr('');
    if (!farmId) { setErr('Sila pilih ladang dahulu.'); return; }
    if (noAccess || readOnly) { setErr('Akaun anda tidak boleh memuat naik fail untuk ladang ini.'); return; }
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    if (filesFor(slot).length + incoming.length > RESIDUE_MAX_PER_SLOT) {
      setErr(`Maksimum ${RESIDUE_MAX_PER_SLOT} fail bagi setiap ruangan.`);
      return;
    }
    const upFarm = farmId, upSeason = sid;             // pin the scope for this batch
    const problems = [];
    setBusySlot(slot);
    try {
      for (const file of incoming) {
        const mime = residueMimeFor(file);
        if (!mime) { problems.push(`"${file.name}": jpeg, png atau pdf sahaja.`); continue; }
        if (file.size > RESIDUE_MAX_BYTES) { problems.push(`"${file.name}" terlalu besar (maks 45 MB).`); continue; }
        const path = `${upFarm}/${upSeason || 0}/${slot}/${Date.now()}-${safeFileName(file.name)}`;
        try {
          // 1. Upload the file (never overwrite an existing object).
          const { error: upErr } = await supabase.storage.from(RESIDUE_BUCKET)
            .upload(path, file, { cacheControl: '3600', upsert: false, contentType: mime });
          if (upErr) { problems.push(`Muat naik "${file.name}" gagal: ${upErr.message || upErr}`); continue; }
          // 2. Record it; if that fails, delete the file so no orphan is left behind.
          const { error: insErr } = await supabase.from(RESIDUE_TABLE).insert({
            farm_id: upFarm,
            season_id: upSeason,
            slot,
            path,
            file_name: file.name || null,
            mime,
            size_bytes: file.size ?? null,
          });
          if (insErr) {
            problems.push(`"${file.name}" gagal direkodkan: ${insErr.message || insErr}`);
            try { await supabase.storage.from(RESIDUE_BUCKET).remove([path]); } catch { /* best effort */ }
          }
        } catch (e) {
          problems.push(`Muat naik "${file.name}" gagal: ${e?.message || e}`);
        }
      }
    } finally {
      setBusySlot(0);
      if (problems.length) setErr(problems.join(' '));
      await reload();
    }
  };

  const handleOpen = async (doc) => {
    setErr('');
    // Open the tab synchronously (inside the click) so pop-up blockers allow it,
    // then point it at a short-lived signed URL.
    let win = null;
    try { win = window.open('', '_blank'); } catch { win = null; }
    if (win) {
      try { win.opener = null; } catch { /* ignore */ }
      try { win.document.write('<p style="font-family:sans-serif;color:#555">Membuka fail…</p>'); } catch { /* ignore */ }
    }
    try {
      const url = await residueSignedUrl(doc.path);
      if (win && !win.closed) {
        win.location.href = url;
      } else {
        // Pop-up was blocked: try a plain new-tab link (never navigate the dashboard away).
        const a = document.createElement('a');
        a.href = url;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    } catch (e) {
      if (win && !win.closed) { try { win.close(); } catch { /* ignore */ } }
      setErr(`Tidak dapat membuka "${doc.file_name || 'fail'}": ${e?.message || e}`);
    }
  };

  const handleRemove = async (doc) => {
    setErr('');
    if (noAccess || readOnly) return;
    if (!window.confirm(`Buang "${doc.file_name || 'fail ini'}"?\n\nFail ini akan dipadam dan tidak boleh dipulihkan.`)) return;
    setBusyId(doc.id);
    try {
      // Storage first, then the row: if the row delete fails the file is simply
      // gone and "Buang" can be pressed again (removing a missing object is a no-op).
      const { error: rmErr } = await supabase.storage.from(RESIDUE_BUCKET).remove([doc.path]);
      if (rmErr) throw rmErr;
      const { data, error: delErr } = await supabase.from(RESIDUE_TABLE).delete().eq('id', doc.id).select('id');
      if (delErr) throw delErr;
      if (!data || !data.length) throw new Error('tiada kebenaran untuk membuang fail ini');
    } catch (e) {
      setErr(`Tidak dapat membuang "${doc.file_name || 'fail'}": ${e?.message || e}`);
    } finally {
      setBusyId(null);
      await reload();
    }
  };

  const partBox = { marginBottom: 10, padding: 10, border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff' };
  const fileChip = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, fontSize: 12, padding: '3px 8px', background: '#f3f4f6', borderRadius: 4, marginTop: 4 };
  const linkBtn = { color: '#2563eb', background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 };
  const anyBusy = !!busySlot || busyId != null;

  return (
    <div>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
        background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)', borderBottom: '2px solid #16a34a', borderRadius: '8px 8px 0 0',
      }}>
        <div>
          <h4 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#15803d' }}>{RESIDUE_HEADING}</h4>
          <div style={{ fontSize: 11, color: '#374151' }}>Kitaran: {cycleLabel || 'Tiada kitaran dipilih'}</div>
        </div>
      </div>
      <div style={{ padding: 12 }}>
        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 10, lineHeight: 1.5 }}>
          Ruangan 1, 3 dan 5 untuk laporan analisis makmal; ruangan 2, 4 dan 6 untuk Borang RF2, bagi cubaan 1 hingga 3.
          Selepas keputusan lulus, ruangan seterusnya tidak perlu diisi. Sistem tidak merekodkan lulus / gagal.
          Semasa Export PDF, fail yang ada digabungkan menjadi satu fail mengikut susunan ruangan.
        </div>
        {noAccess && (
          <div style={{ fontSize: 12, color: '#92400e', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 4, padding: '6px 8px', marginBottom: 8 }}>
            Akaun anda tiada akses kepada laporan residu ladang ini (tiada peranan pada ladang ini). Hubungi pentadbir ladang untuk akses.
          </div>
        )}
        {readOnly && (
          <div style={{ fontSize: 12, color: '#4b5563', background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 4, padding: '6px 8px', marginBottom: 8 }}>
            Baca sahaja: akaun anda boleh membuka fail tetapi tidak boleh memuat naik atau membuang.
          </div>
        )}
        {status === 'loading' && <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>Memuatkan senarai fail…</div>}
        {status === 'error' && (
          <div style={{ fontSize: 12, color: '#b91c1c', background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 4, padding: '6px 8px', marginBottom: 8 }}>
            Senarai fail tidak dapat dimuatkan ({loadErr}).{' '}
            <button type="button" onClick={() => { setStatus('loading'); reload(); }}
              style={{ padding: '1px 6px', fontSize: 11, background: '#fff', border: '1px solid #fecaca', borderRadius: 4, cursor: 'pointer' }}>Cuba lagi</button>
          </div>
        )}
        {err && <div style={{ fontSize: 12, color: '#b91c1c', background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 4, padding: '6px 8px', marginBottom: 8 }}>{err}</div>}
        {RESIDUE_SLOTS.map(({ slot, label }) => {
          const files = filesFor(slot);
          return (
            <div key={slot} style={partBox}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{slot}. {label}</div>
              <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 6 }}>{files.length}/{RESIDUE_MAX_PER_SLOT} fail (jpeg/png/pdf, maks 45 MB)</div>
              <input
                type="file"
                accept="image/jpeg,image/png,application/pdf"
                multiple
                disabled={noAccess || readOnly || status !== 'ready' || anyBusy || files.length >= RESIDUE_MAX_PER_SLOT}
                onChange={(e) => { handleUpload(slot, e.target.files); e.target.value = ''; }}
                style={{ fontSize: 12 }}
              />
              {busySlot === slot && <span style={{ marginLeft: 8, fontSize: 12, color: '#6b7280' }}>Memuat naik…</span>}
              {files.map((f) => (
                <div key={f.id} style={fileChip}>
                  <button type="button" onClick={() => handleOpen(f)} style={linkBtn} title="Buka dalam tab baharu">
                    {(f.mime === 'application/pdf' || /\.pdf$/i.test(f.file_name || '')) ? '📄' : '🖼️'} {f.file_name || f.path.split('/').pop()}
                  </button>
                  {!readOnly && (
                  <button type="button" onClick={() => handleRemove(f)} disabled={anyBusy}
                    style={{ padding: '1px 6px', fontSize: 11, background: '#fee2e2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 4, cursor: anyBusy ? 'wait' : 'pointer', flexShrink: 0 }}>
                    {busyId === f.id ? 'Membuang…' : 'Buang'}
                  </button>
                  )}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MyGAPResidueSection;
